import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';

const TEST_PRINTS_PRODUCT_TYPE = 'test_prints';
const LABEL_MAP = { printSize: 'Print Size' };

const TestPrintsFormInputs = ({ quantityRef }) => {
  const { form, setForm } = useProductSelectionStore();

  const { data: paperTypesData } = useQuery({
    queryKey: ['paperTypes'],
    queryFn: () => httpClient.get('products/papers'),
    staleTime: Infinity,
    cacheTime: 0,
  });

  const firstPaperTypeId = paperTypesData?.data?.[0]?.id;

  const { data: printSizesData, isLoading: printSizesLoading } = useQuery({
    queryKey: ['printSizes', firstPaperTypeId],
    queryFn: () => httpClient.get(`products/${firstPaperTypeId}/print_sizes`),
    enabled: !!firstPaperTypeId,
    staleTime: Infinity,
    cacheTime: 0,
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, xAspectRatio: 5, yAspectRatio: 7, aspectRatio: 5 / 7 },
      inputs: {
        ...prev.inputs,
        printSize: {
          ...prev.inputs?.printSize,
          isLoading: true,
          isDisabled: true,
          options: [],
        },
      },
    }));
  }, [setForm]);

  useEffect(() => {
    if (!printSizesData || printSizesLoading) return;

    const size5x7 = printSizesData.find(
      (ps) =>
        ps.productType === TEST_PRINTS_PRODUCT_TYPE &&
        ps.name?.replace(/\s/g, '').toLowerCase() === '5x7',
    );

    const printSizeValue = size5x7?.guid;
    const printSizePrice = Number(size5x7?.price);
    const printSizeText = size5x7
      ? `${size5x7.name} ($${printSizePrice.toFixed(2)})`
      : undefined;

    setForm((prev) => {
      const current = prev?.inputs?.printSize;
      if (
        current?.selectedValue === printSizeValue &&
        Number(current?.selectedValuePrice) === printSizePrice &&
        !current?.isLoading
      ) return prev;

      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          printSize: {
            label: 'Print Size',
            options: size5x7
              ? [{ value: printSizeValue, text: printSizeText, price: printSizePrice }]
              : [],
            selectedValue: printSizeValue,
            selectedValueText: printSizeText,
            selectedValuePrice: size5x7 ? printSizePrice : undefined,
            isDisabled: true,
            isLoading: false,
          },
        },
      };
    });
  }, [printSizesData, printSizesLoading, setForm]);

  if (!form || !form.inputs) return null;

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {Object.keys(form.inputs).map((key) => {
        const input = form.inputs[key];
        if (key === 'finishingService' || key === 'paperType' || !input) return null;

        return (
          <div key={key} className="input-container paper-type">
            <div className="label-container">
              <div className="div-block-150">
                <div className="sub-label-form">{input.label || LABEL_MAP[key] || key}</div>
              </div>
            </div>
            <div className="form-block-input w-form">
              <form className="form-input select-field">
                <select
                  className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                  style={{ width: '100%' }}
                  value={input.selectedValue || 'default'}
                  disabled={input.isDisabled}
                  onChange={() => {}}
                >
                  {input.isLoading && !input.options?.length ? (
                    <option value="">Loading...</option>
                  ) : (
                    <option value="default">Choose One</option>
                  )}
                  {(input.options || [])
                    .filter((option) => option?.text && option.value !== 'default')
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                </select>
                <img
                  src={ChevronIcon}
                  alt=""
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    width: '16px',
                    height: '16px',
                  }}
                />
              </form>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TestPrintsFormInputs;
